import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronLeft, Loader2, Sparkles, UploadCloud, FileText, Check, Trash2, Camera, Wand2, Edit3, Search, Inbox, MessageSquare, Mic } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import {
  parseRecipe, publishRecipe, updateRecipe, listRecipes, extractPdfText,
  getAdminPassword, setAdminPassword, clearAdminPassword, checkAdminAuth,
  listSuggestions, dismissSuggestion, draftFromSuggestion,
  regenerateWelcomeAudio,
} from "@/lib/adminApi";

// ==== Admin Panel: solo pa' papa ====================================
// Flujo en 4 pasos:
//   1) Login admin (password separada de la de miembro)
//   2) Pegar texto o subir PDF
//   3) Preview editable del JSON parseado por Papi (Llama 3.3)
//   4) Foto del plato + publicar (commit + audios PVC + push a familia)

const CATEGORIES = [
  "Bebidas y Cocktails",
  "Carnes y Pollos",
  "Desayunos y Brunch",
  "Entradas, Aperitivos y Sopas",
  "Festividades Judías",
  "Guarniciones y Arroces",
  "Helados y Sorbetes",
  "Panes, Empanadas y Fritos",
  "Pescados y Mariscos",
  "Platos Caseros y Vegetarianos",
  "Platos Internacionales",
  "Postres y Tortas",
  "Salsas, Aderezos y Básicos",
];

function compressPhoto(file, maxSize = 1600, quality = 0.85) {
  return new Promise((resolve, reject) => {
    const img = document.createElement("img");
    const url = URL.createObjectURL(file);
    img.onload = () => {
      let { width, height } = img;
      if (width > maxSize || height > maxSize) {
        const scale = maxSize / Math.max(width, height);
        width = Math.round(width * scale);
        height = Math.round(height * scale);
      }
      const canvas = document.createElement("canvas");
      canvas.width = width; canvas.height = height;
      canvas.getContext("2d").drawImage(img, 0, 0, width, height);
      URL.revokeObjectURL(url);
      canvas.toBlob((blob) => {
        const fr = new FileReader();
        fr.onload = () => resolve(fr.result);
        fr.onerror = reject;
        fr.readAsDataURL(blob);
      }, "image/jpeg", quality);
    };
    img.onerror = reject;
    img.src = url;
  });
}

export default function Admin() {
  const { member, logout } = useAuth();
  const nav = useNavigate();
  const [authed, setAuthed] = useState(false);
  const [pwInput, setPwInput] = useState("");
  const [pwChecking, setPwChecking] = useState(false);

  // Solo papa puede entrar
  useEffect(() => {
    if (member && member !== "papa") {
      toast.error("Solo Papi puede acceder al Admin");
      nav("/cocina");
      return;
    }
    const saved = getAdminPassword();
    if (saved) {
      checkAdminAuth(saved).then((ok) => setAuthed(!!ok));
    }
  }, [member, nav]);

  const doLogin = async () => {
    if (!pwInput.trim()) return;
    setPwChecking(true);
    try {
      const ok = await checkAdminAuth(pwInput.trim());
      if (ok) {
        setAdminPassword(pwInput.trim());
        setAuthed(true);
        toast.success("Bienvenido al Admin, Papi 👨‍🍳");
      } else {
        toast.error("Clave incorrecta");
      }
    } catch { toast.error("Error de red"); }
    finally { setPwChecking(false); }
  };

  const doLogout = () => {
    clearAdminPassword();
    setAuthed(false);
    setPwInput("");
  };

  return (
    <div className="min-h-screen paper-texture iphone-safe-top">
      <header className="sticky top-0 z-30 bg-paper/95 backdrop-blur-md border-b border-line">
        <div className="max-w-3xl mx-auto px-5 py-3 flex items-center justify-between">
          <Link data-testid="admin-back-btn" to="/cocina" className="p-2 rounded-full bg-cream border border-line">
            <ChevronLeft className="w-5 h-5 text-cocoa" />
          </Link>
          <div className="text-center">
            <p className="eyebrow text-[10px]">Panel privado</p>
            <h1 className="font-display italic text-cocoa text-2xl leading-tight">Admin del Pollo</h1>
          </div>
          {authed ? (
            <button data-testid="admin-logout" onClick={doLogout} className="text-xs italic text-cocoa-soft hover:underline">
              Cerrar
            </button>
          ) : <div className="w-9 h-9" />}
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 pb-20 pt-6">
        {authed && (
          <Link data-testid="admin-calendar-link" to="/calendario/admin" className="flex items-center justify-between gap-3 rounded-2xl bg-night text-white px-5 py-4 mb-6">
            <span>
              <span className="block font-display italic text-xl">Calendario Familiar</span>
              <span className="block text-xs text-white/70">Personas, fechas, correcciones y claves de la familia</span>
            </span>
            <ChevronLeft className="w-5 h-5 rotate-180 text-candle" />
          </Link>
        )}
        {!authed && (
          <div className="bg-cream border border-line rounded-2xl p-6 max-w-md mx-auto">
            <h2 className="font-display italic text-cocoa text-2xl mb-2">Clave de administrador</h2>
            <p className="font-serif text-cocoa-soft text-sm italic mb-4">
              Es una clave privada, solo tuya (distinta de la de login).
            </p>
            <input
              data-testid="admin-password-input"
              type="password"
              value={pwInput}
              onChange={(e) => setPwInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && doLogin()}
              placeholder="tu clave secreta"
              className="w-full px-4 py-3 rounded-lg bg-paper border border-line text-cocoa font-serif focus:outline-none focus:ring-2 focus:ring-amber-deep/40 mb-3"
            />
            <button
              data-testid="admin-login-btn"
              onClick={doLogin}
              disabled={pwChecking || !pwInput.trim()}
              className="btn-primary w-full"
            >
              {pwChecking ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Entrar
            </button>
          </div>
        )}

        {authed && <AdminAuthed />}
      </main>
    </div>
  );
}

// ============================================================
// Zona autenticada: wizard de nueva receta / edición
// ============================================================
function AdminAuthed() {
  const [mode, setMode] = useState("choose"); // "choose" | "create" | "edit-picker" | "edit" | "suggestions" | "welcome"
  const [step, setStep] = useState(1); // 1=input, 2=preview, 3=success
  const [inputMode, setInputMode] = useState("text");
  const [text, setText] = useState("");
  const [parsing, setParsing] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [recipe, setRecipe] = useState(null);
  const [editingSlug, setEditingSlug] = useState(null); // si tiene valor, estamos editando
  const [duplicates, setDuplicates] = useState({ exact: null, similar: [] });
  const [showDupModal, setShowDupModal] = useState(false);
  const [photoDataUrl, setPhotoDataUrl] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const [publishResult, setPublishResult] = useState(null);
  const pdfInputRef = useRef(null);
  const photoInputRef = useRef(null);

  const resetAll = () => {
    setMode("choose"); setStep(1); setInputMode("text"); setText(""); setRecipe(null);
    setEditingSlug(null);
    setDuplicates({ exact: null, similar: [] }); setShowDupModal(false);
    setPhotoDataUrl(null); setPhotoPreview(null); setPublishResult(null);
  };

  const handlePdfPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setPdfBusy(true);
    try {
      const t = await extractPdfText(f);
      if (!t || t.length < 30) {
        toast.error("El PDF no tiene texto suficiente. ¿Es un PDF de imágenes?");
      } else {
        setText(t);
        setInputMode("text");
        toast.success(`Extraído: ${t.length} caracteres del PDF`);
      }
    } catch (err) {
      toast.error("Error leyendo el PDF: " + (err.message || err));
    } finally { setPdfBusy(false); e.target.value = ""; }
  };

  const handleParse = async () => {
    if (text.trim().length < 30) return toast.error("Muy corto — necesita al menos 30 caracteres");
    setParsing(true);
    try {
      const result = await parseRecipe(text);
      setRecipe(result.recipe);
      setDuplicates(result.duplicates || { exact: null, similar: [] });
      // Si hay match exacto O similares, muestro modal antes de ir al preview
      const hasDup = result.duplicates?.exact || (result.duplicates?.similar || []).length > 0;
      if (hasDup) {
        setShowDupModal(true);
      } else {
        setStep(2);
        toast.success("Papi leyó la receta, revísala 👇");
      }
    } catch (e) { toast.error("Papi no entendió: " + (e.message || e)); }
    finally { setParsing(false); }
  };

  const acceptDupAndContinue = () => {
    setShowDupModal(false);
    setStep(2);
    toast("Recuerda cambiar el nombre pa' que no choque con la existente", { icon: "⚠️" });
  };

  const cancelDup = () => {
    setShowDupModal(false);
    setRecipe(null);
    setDuplicates({ exact: null, similar: [] });
  };

  const handlePhotoPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/^image\/(jpeg|png|webp)$/.test(f.type)) return toast.error("JPG, PNG o WEBP");
    try {
      const dataUrl = await compressPhoto(f);
      setPhotoDataUrl(dataUrl);
      setPhotoPreview(dataUrl);
    } catch (err) { toast.error("Error comprimiendo foto: " + (err.message || err)); }
    e.target.value = "";
  };

  const handlePublish = async () => {
    if (!recipe) return;
    if (!editingSlug && !photoDataUrl) return toast.error("Falta la foto del plato");
    setPublishing(true);
    try {
      const fn = editingSlug ? updateRecipe : publishRecipe;
      const result = await fn(recipe, photoDataUrl);
      setPublishResult(result);
      setStep(3);
      if (editingSlug) {
        toast.success(`Actualizada. ${result.audios?.regenerated || 0} audios regenerados`);
      } else {
        toast.success(`¡Publicada! ${result.audios?.generated || 0} audios generados`);
      }
    } catch (e) { toast.error("No se pudo: " + (e.message || e)); }
    finally { setPublishing(false); }
  };

  const pickRecipeToEdit = (r) => {
    setRecipe({ ...r, sin_gluten: !!r.sin_gluten });
    setEditingSlug(r.slug);
    setPhotoPreview(r.photo); // muestra la foto actual como preview
    setPhotoDataUrl(null);    // pero no la manda si no se cambia
    setMode("edit");
    setStep(2); // salta directo al preview editable
  };

  // ============= UI ================
  if (mode === "choose") {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <button
          data-testid="admin-mode-create"
          onClick={() => setMode("create")}
          className="bg-cream border border-line rounded-2xl p-8 text-left hover:border-amber-deep transition"
        >
          <div className="w-12 h-12 rounded-full bg-amber-deep/15 flex items-center justify-center mb-3">
            <Sparkles className="w-6 h-6 text-amber-deep" />
          </div>
          <h2 className="font-display italic text-cocoa text-2xl mb-1">Crear nueva receta</h2>
          <p className="font-serif text-cocoa-soft text-sm italic">
            Pega un texto o sube un PDF y Papi lo deja listo para el recetario.
          </p>
        </button>
        <button
          data-testid="admin-mode-edit"
          onClick={() => setMode("edit-picker")}
          className="bg-cream border border-line rounded-2xl p-8 text-left hover:border-amber-deep transition"
        >
          <div className="w-12 h-12 rounded-full bg-amber-deep/15 flex items-center justify-center mb-3">
            <Edit3 className="w-6 h-6 text-amber-deep" />
          </div>
          <h2 className="font-display italic text-cocoa text-2xl mb-1">Editar receta existente</h2>
          <p className="font-serif text-cocoa-soft text-sm italic">
            Ajusta ingredientes, pasos, foto o notas. Regenera solo los audios que cambian.
          </p>
        </button>
        <button
          data-testid="admin-mode-suggestions"
          onClick={() => setMode("suggestions")}
          className="bg-cream border border-line rounded-2xl p-8 text-left hover:border-amber-deep transition"
        >
          <div className="w-12 h-12 rounded-full bg-amber-deep/15 flex items-center justify-center mb-3">
            <Inbox className="w-6 h-6 text-amber-deep" />
          </div>
          <h2 className="font-display italic text-cocoa text-2xl mb-1">Sugerencias de la familia</h2>
          <p className="font-serif text-cocoa-soft text-sm italic">
            Revisa qué recetas quieren que agregues. Papi puede armarte un borrador con IA para que solo lo pulas y publiques.
          </p>
        </button>
        <button
          data-testid="admin-mode-welcome"
          onClick={() => setMode("welcome")}
          className="bg-cream border border-line rounded-2xl p-8 text-left hover:border-amber-deep transition"
        >
          <div className="w-12 h-12 rounded-full bg-amber-deep/15 flex items-center justify-center mb-3">
            <Mic className="w-6 h-6 text-amber-deep" />
          </div>
          <h2 className="font-display italic text-cocoa text-2xl mb-1">Saludos de bienvenida</h2>
          <p className="font-serif text-cocoa-soft text-sm italic">
            Regenera el audio de bienvenida de cualquier miembro con tu voz clonada. Ideal si cambias el texto o el archivo se dañó.
          </p>
        </button>
      </div>
    );
  }

  if (mode === "welcome") {
    return <WelcomeAudios onBack={() => setMode("choose")} />;
  }

  if (mode === "suggestions") {
    return <SuggestionsInbox onBack={() => setMode("choose")} onLoadDraft={(recipeDraft) => {
      // Cargar el borrador directo al step 2 como si fuera parseRecipe
      setRecipe(recipeDraft);
      setDuplicates({ exact: null, similar: [] });
      setMode("create");
      setStep(2);
      toast.success("Borrador cargado. Revísalo, ajústalo y publícalo 💛");
    }} />;
  }

  if (mode === "edit-picker") {
    return <EditPicker onPick={pickRecipeToEdit} onBack={() => setMode("choose")} />;
  }

  if (step === 3 && publishResult) {
    return (
      <div className="bg-cream border-2 border-amber-deep/40 rounded-2xl p-6 text-center">
        <div className="w-16 h-16 rounded-full bg-amber-deep/15 flex items-center justify-center mx-auto mb-4">
          <Check className="w-8 h-8 text-amber-deep" />
        </div>
        <h2 className="font-display italic text-cocoa text-3xl mb-2">
          {editingSlug ? "¡Actualizada!" : "¡Publicada!"}
        </h2>
        <p className="font-serif text-cocoa-soft italic mb-4">
          <b>{recipe.nombre}</b>
          {editingSlug
            ? ` — ${publishResult.audios?.regenerated || 0} audios regenerados${publishResult.audios?.deltas?.length ? ` (${publishResult.audios.deltas.join(", ")})` : ""}.`
            : ` ya está en el recetario, con ${publishResult.audios?.generated || 0} audios PVC. La familia recibió el aviso por notificación.`}
        </p>
        <p className="text-xs italic text-cocoa-soft mb-6">
          Vercel deploya en ~1 min. Después la ves actualizada en el listado.
        </p>
        <div className="flex flex-col gap-2 max-w-xs mx-auto">
          <Link to={`/receta/${publishResult.slug}`} className="btn-primary">Ver la receta</Link>
          <button data-testid="admin-new-btn" onClick={resetAll} className="btn-ghost">Volver al inicio</button>
        </div>
      </div>
    );
  }

  if (step === 2 && recipe) {
    return (
      <AdminPreview
        recipe={recipe}
        onChange={setRecipe}
        onBack={() => editingSlug ? resetAll() : setStep(1)}
        onContinue={handlePublish}
        photoDataUrl={photoDataUrl}
        photoPreview={photoPreview}
        onPhotoPick={handlePhotoPick}
        photoInputRef={photoInputRef}
        publishing={publishing}
        categories={CATEGORIES}
        isEditing={!!editingSlug}
      />
    );
  }

  // Step 1: input
  return (
    <div className="space-y-4">
      {showDupModal && (
        <DuplicateModal
          duplicates={duplicates}
          onCancel={cancelDup}
          onContinue={acceptDupAndContinue}
        />
      )}
      <div className="bg-cream border border-line rounded-2xl p-6">
        <h2 className="font-display italic text-cocoa text-2xl mb-1">Nueva receta</h2>
        <p className="font-serif text-cocoa-soft text-sm italic mb-4">
          Pega el texto de la receta (o sube un PDF) y Papi la deja en formato del recetario.
        </p>

        <div className="flex gap-2 mb-4">
          <button
            data-testid="admin-mode-text"
            onClick={() => setInputMode("text")}
            className={`flex-1 px-3 py-2 rounded-full font-serif text-sm border transition ${inputMode === "text" ? "bg-amber-deep text-cream border-amber-deep" : "bg-paper text-cocoa border-line hover:border-amber-deep"}`}
          >
            <FileText className="w-4 h-4 inline mr-1" /> Pegar texto
          </button>
          <button
            data-testid="admin-mode-pdf"
            onClick={() => pdfInputRef.current?.click()}
            className="flex-1 px-3 py-2 rounded-full font-serif text-sm border bg-paper text-cocoa border-line hover:border-amber-deep transition"
          >
            {pdfBusy ? <Loader2 className="w-4 h-4 inline mr-1 animate-spin" /> : <UploadCloud className="w-4 h-4 inline mr-1" />}
            {pdfBusy ? "Leyendo…" : "Subir PDF"}
          </button>
          <input
            ref={pdfInputRef}
            data-testid="admin-pdf-input"
            type="file"
            accept="application/pdf"
            onChange={handlePdfPick}
            className="hidden"
          />
        </div>

        <textarea
          data-testid="admin-text-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={16}
          placeholder="Pega acá el texto de la receta. Ejemplo:&#10;&#10;Pollo Ganso Braseado Asiático...&#10;Rinde: 4-5 porciones&#10;Preparación: 20 min&#10;Cocción: 55 min&#10;&#10;Ingredientes:&#10;- 1,2 kg de pollo ganso&#10;..."
          className="w-full px-4 py-3 rounded-lg bg-paper border border-line text-cocoa font-serif text-sm focus:outline-none focus:ring-2 focus:ring-amber-deep/40 resize-y"
        />

        <div className="flex justify-between items-center mt-4">
          <p className="text-xs italic text-cocoa-soft">{text.length} caracteres</p>
          <button
            data-testid="admin-parse-btn"
            onClick={handleParse}
            disabled={parsing || text.trim().length < 30}
            className="btn-primary"
          >
            {parsing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
            {parsing ? "Papi está leyendo…" : "Analizar con Papi"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Preview editable + publish
// ============================================================
function AdminPreview({ recipe, onChange, onBack, onContinue, photoDataUrl, photoPreview, onPhotoPick, photoInputRef, publishing, categories, isEditing }) {
  const set = (k, v) => onChange({ ...recipe, [k]: v });
  const setListItem = (k, idx, val) => {
    const arr = [...(recipe[k] || [])];
    arr[idx] = val;
    onChange({ ...recipe, [k]: arr });
  };
  const removeListItem = (k, idx) => {
    const arr = [...(recipe[k] || [])];
    arr.splice(idx, 1);
    onChange({ ...recipe, [k]: arr });
  };
  const addListItem = (k) => onChange({ ...recipe, [k]: [...(recipe[k] || []), ""] });

  return (
    <div className="space-y-4">
      <div className="bg-cream border border-line rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="w-4 h-4 text-amber-deep" />
          <p className="eyebrow">Preview editable</p>
        </div>

        <label className="block mb-4">
          <span className="text-xs italic text-cocoa-soft mb-1 block">Nombre</span>
          <input
            data-testid="admin-preview-nombre"
            type="text"
            value={recipe.nombre || ""}
            onChange={(e) => set("nombre", e.target.value)}
            className="w-full px-3 py-2 rounded-md bg-paper border border-line font-serif text-cocoa focus:outline-none focus:ring-2 focus:ring-amber-deep/40"
          />
        </label>

        <div className="grid grid-cols-2 gap-3 mb-4">
          <label className="block">
            <span className="text-xs italic text-cocoa-soft mb-1 block">Categoría</span>
            <select
              data-testid="admin-preview-categoria"
              value={recipe.categoria || ""}
              onChange={(e) => set("categoria", e.target.value)}
              className="w-full px-3 py-2 rounded-md bg-paper border border-line font-serif text-cocoa"
            >
              <option value="">— elige —</option>
              {categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-xs italic text-cocoa-soft mb-1 block">Origen</span>
            <input
              data-testid="admin-preview-origen"
              type="text"
              value={recipe.origen || ""}
              onChange={(e) => set("origen", e.target.value)}
              placeholder="Chilena, Italiana…"
              className="w-full px-3 py-2 rounded-md bg-paper border border-line font-serif text-cocoa"
            />
          </label>
        </div>

        <div className="grid grid-cols-3 gap-3 mb-4">
          <label className="block">
            <span className="text-xs italic text-cocoa-soft mb-1 block">Porciones</span>
            <input value={recipe.porciones || ""} onChange={(e) => set("porciones", e.target.value)} className="w-full px-3 py-2 rounded-md bg-paper border border-line font-serif text-cocoa" />
          </label>
          <label className="block">
            <span className="text-xs italic text-cocoa-soft mb-1 block">Prep</span>
            <input value={recipe.tiempo_preparacion || ""} onChange={(e) => set("tiempo_preparacion", e.target.value)} className="w-full px-3 py-2 rounded-md bg-paper border border-line font-serif text-cocoa" />
          </label>
          <label className="block">
            <span className="text-xs italic text-cocoa-soft mb-1 block">Cocción</span>
            <input value={recipe.tiempo_coccion || ""} onChange={(e) => set("tiempo_coccion", e.target.value)} className="w-full px-3 py-2 rounded-md bg-paper border border-line font-serif text-cocoa" />
          </label>
        </div>

        <label className="block mb-4">
          <span className="text-xs italic text-cocoa-soft mb-1 block">Descripción corta</span>
          <textarea
            data-testid="admin-preview-descripcion"
            value={recipe.descripcion_corta || ""}
            onChange={(e) => set("descripcion_corta", e.target.value)}
            rows={2}
            className="w-full px-3 py-2 rounded-md bg-paper border border-line font-serif text-cocoa"
          />
        </label>

        <div className="flex items-center gap-4 mb-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={!!recipe.sin_gluten}
              onChange={(e) => set("sin_gluten", e.target.checked)}
              className="w-4 h-4"
            />
            <span className="text-sm font-serif text-cocoa">Sin gluten</span>
          </label>
          <label className="block flex-1">
            <span className="text-xs italic text-cocoa-soft mb-1 block">Dificultad</span>
            <select value={recipe.dificultad || "Media"} onChange={(e) => set("dificultad", e.target.value)} className="w-full px-3 py-2 rounded-md bg-paper border border-line font-serif text-cocoa">
              <option>Fácil</option><option>Media</option><option>Difícil</option>
            </select>
          </label>
        </div>

        <ListEditor label="Ingredientes" items={recipe.ingredientes || []} onSet={(i, v) => setListItem("ingredientes", i, v)} onRemove={(i) => removeListItem("ingredientes", i)} onAdd={() => addListItem("ingredientes")} testidPrefix="ingr" />
        <ListEditor label="Instrucciones" items={recipe.instrucciones || []} onSet={(i, v) => setListItem("instrucciones", i, v)} onRemove={(i) => removeListItem("instrucciones", i)} onAdd={() => addListItem("instrucciones")} testidPrefix="inst" textarea />

        <label className="block mt-4">
          <span className="text-xs italic text-cocoa-soft mb-1 block">Notas (opcional)</span>
          <textarea value={recipe.notas || ""} onChange={(e) => set("notas", e.target.value)} rows={3} className="w-full px-3 py-2 rounded-md bg-paper border border-line font-serif text-cocoa" />
        </label>

        <label className="block mt-4">
          <span className="text-xs italic text-cocoa-soft mb-1 block">Tags (separados por coma)</span>
          <input
            value={(recipe.tags || []).join(", ")}
            onChange={(e) => set("tags", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
            className="w-full px-3 py-2 rounded-md bg-paper border border-line font-serif text-cocoa"
          />
        </label>
      </div>

      {/* Foto */}
      <div className="bg-cream border border-line rounded-2xl p-6">
        <h3 className="font-display italic text-cocoa text-xl mb-2">
          Foto del plato{isEditing && " (opcional al editar)"}
        </h3>
        <input
          ref={photoInputRef}
          data-testid="admin-photo-input"
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={onPhotoPick}
          className="hidden"
        />
        {!photoPreview && (
          <button
            data-testid="admin-photo-pick"
            onClick={() => photoInputRef.current?.click()}
            className="w-full border-2 border-dashed border-amber-deep/40 rounded-xl py-10 flex flex-col items-center gap-2 text-cocoa hover:border-amber-deep transition"
          >
            <Camera className="w-8 h-8 text-amber-deep" />
            <span className="font-serif">Tomar o elegir foto</span>
            <span className="text-xs italic text-cocoa-soft">JPG, PNG o WEBP — se comprime automáticamente</span>
          </button>
        )}
        {photoPreview && (
          <>
            <img src={photoPreview} alt="Foto plato" className="w-full aspect-square object-cover rounded-xl border border-line mb-3" />
            {isEditing && !photoDataUrl && (
              <p className="text-xs italic text-cocoa-soft text-center mb-3">
                Esta es la foto actual. Solo se cambia si eliges una nueva.
              </p>
            )}
            <button
              data-testid="admin-photo-replace"
              onClick={() => photoInputRef.current?.click()}
              className="btn-ghost w-full"
            >Cambiar foto</button>
          </>
        )}
      </div>

      <div className="flex gap-3 sticky bottom-4">
        <button data-testid="admin-back-step" onClick={onBack} className="btn-ghost flex-1" disabled={publishing}>Volver</button>
        <button
          data-testid="admin-publish-btn"
          onClick={onContinue}
          disabled={publishing || (!isEditing && !photoDataUrl)}
          className="btn-primary flex-1"
        >
          {publishing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
          {publishing ? (isEditing ? "Actualizando…" : "Publicando…") : (isEditing ? "Guardar cambios" : "Publicar receta")}
        </button>
      </div>
    </div>
  );
}

function ListEditor({ label, items, onSet, onRemove, onAdd, testidPrefix, textarea }) {
  return (
    <div className="mt-4">
      <div className="flex justify-between items-center mb-2">
        <span className="text-xs italic text-cocoa-soft">{label} ({items.length})</span>
        <button
          data-testid={`admin-${testidPrefix}-add`}
          onClick={onAdd}
          className="text-xs italic text-amber-deep hover:underline"
        >+ agregar</button>
      </div>
      <ul className="space-y-2">
        {items.map((v, i) => (
          <li key={i} className="flex gap-2 items-start">
            <span className="mt-2 text-xs text-cocoa-soft italic w-6 shrink-0">{i + 1}.</span>
            {textarea ? (
              <textarea
                data-testid={`admin-${testidPrefix}-${i}`}
                value={v}
                onChange={(e) => onSet(i, e.target.value)}
                rows={2}
                className="flex-1 px-3 py-2 rounded-md bg-paper border border-line font-serif text-cocoa text-sm"
              />
            ) : (
              <input
                data-testid={`admin-${testidPrefix}-${i}`}
                type="text"
                value={v}
                onChange={(e) => onSet(i, e.target.value)}
                className="flex-1 px-3 py-2 rounded-md bg-paper border border-line font-serif text-cocoa text-sm"
              />
            )}
            <button onClick={() => onRemove(i)} className="p-2 text-cocoa-soft hover:text-red-600" aria-label="Borrar">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ==== Modal de recetas duplicadas / similares =========================
function DuplicateModal({ duplicates, onCancel, onContinue }) {
  const { exact, similar } = duplicates;
  return (
    <div
      data-testid="admin-dup-modal"
      className="fixed inset-0 z-50 bg-cocoa/60 flex items-center justify-center p-4"
      onClick={onCancel}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-paper rounded-2xl border border-line w-full max-w-lg p-6"
      >
        {exact ? (
          <>
            <h3 className="font-display italic text-cocoa text-2xl mb-2">
              Ojo, ya existe esta receta
            </h3>
            <p className="font-serif text-cocoa-soft italic mb-4">
              Papi detectó una receta con el mismo nombre y slug:
            </p>
            <a
              data-testid="admin-dup-exact-link"
              href={`/receta/${exact.slug}`}
              target="_blank"
              rel="noreferrer"
              className="block bg-cream border border-amber-deep/40 rounded-xl p-3 mb-4 hover:border-amber-deep transition"
            >
              <p className="font-display italic text-cocoa text-lg leading-tight">{exact.nombre}</p>
              <p className="text-xs italic text-cocoa-soft">/receta/{exact.slug} · abre en pestaña nueva</p>
            </a>
          </>
        ) : (
          <>
            <h3 className="font-display italic text-cocoa text-2xl mb-2">
              Recetas parecidas
            </h3>
            <p className="font-serif text-cocoa-soft italic mb-4">
              Encontré recetas con nombres similares. Revisa que no sea la misma:
            </p>
          </>
        )}

        {similar && similar.length > 0 && (
          <ul className="space-y-2 mb-4">
            {similar.map((s) => (
              <li key={s.slug}>
                <a
                  href={`/receta/${s.slug}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`admin-dup-similar-${s.slug}`}
                  className="block bg-cream border border-line rounded-lg p-3 hover:border-amber-deep transition"
                >
                  <p className="font-serif text-cocoa text-sm">{s.nombre}</p>
                  <p className="text-xs italic text-cocoa-soft">/receta/{s.slug}</p>
                </a>
              </li>
            ))}
          </ul>
        )}

        <p className="text-xs italic text-cocoa-soft mb-4">
          {exact
            ? "Si continúas, tienes que cambiar el nombre (y el slug) pa' no chocar. Si no, la publicación fallará."
            : "Si es la misma, cancela. Si es una versión distinta (ej. sin gluten, con otro corte), continúa igual."}
        </p>

        <div className="flex gap-2">
          <button
            data-testid="admin-dup-cancel"
            onClick={onCancel}
            className="btn-ghost flex-1"
          >
            Cancelar
          </button>
          <button
            data-testid="admin-dup-continue"
            onClick={onContinue}
            className="btn-primary flex-1"
          >
            Continuar igual
          </button>
        </div>
      </div>
    </div>
  );
}


// ==== Selector de receta a editar ====================================
function EditPicker({ onPick, onBack }) {
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    listRecipes().then((rs) => {
      setRecipes(rs);
      setLoading(false);
    }).catch(() => { toast.error("No pude cargar el listado"); setLoading(false); });
  }, []);

  const norm = (s) => String(s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  const filtered = useMemo(() => {
    if (!q.trim()) return recipes;
    const nq = norm(q);
    return recipes.filter((r) => norm(r.nombre).includes(nq) || norm(r.slug).includes(nq));
  }, [recipes, q]);

  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <button data-testid="edit-picker-back" onClick={onBack} className="btn-ghost">
          <ChevronLeft className="w-4 h-4" /> Volver
        </button>
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-cocoa-soft" />
          <input
            data-testid="edit-picker-search"
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Busca una receta pa' editar…"
            className="w-full pl-10 pr-3 py-2 rounded-full bg-paper border border-line font-serif text-cocoa focus:outline-none focus:ring-2 focus:ring-amber-deep/40"
          />
        </div>
      </div>

      {loading && <p className="text-center italic text-cocoa-soft py-10">Cargando…</p>}
      {!loading && filtered.length === 0 && <p className="text-center italic text-cocoa-soft py-10">Ninguna coincide</p>}

      <ul data-testid="edit-picker-list" className="space-y-2">
        {filtered.slice(0, 50).map((r) => (
          <li key={r.slug}>
            <button
              data-testid={`edit-picker-${r.slug}`}
              onClick={() => onPick(r)}
              className="w-full flex items-center gap-3 p-3 bg-cream border border-line rounded-lg hover:border-amber-deep transition text-left"
            >
              {r.photo && (
                <img src={r.photo} alt={r.nombre} className="w-14 h-14 object-cover rounded-md shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <p className="font-display italic text-cocoa text-base truncate">{r.nombre}</p>
                <p className="text-xs italic text-cocoa-soft truncate">{r.categoria}{r.origen ? ` · ${r.origen}` : ""}</p>
              </div>
              <Edit3 className="w-4 h-4 text-amber-deep shrink-0" />
            </button>
          </li>
        ))}
      </ul>
      {filtered.length > 50 && (
        <p className="text-xs italic text-cocoa-soft text-center mt-3">Mostrando 50 de {filtered.length}. Refina la búsqueda.</p>
      )}
    </div>
  );
}


// ==== Bandeja de sugerencias de la familia ============================
function SuggestionsInbox({ onBack, onLoadDraft }) {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draftingId, setDraftingId] = useState(null);
  const [dismissingId, setDismissingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const list = await listSuggestions();
      setSuggestions(list);
    } catch (e) {
      toast.error("No se pudo cargar: " + (e.message || e));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const doDraft = async (s) => {
    setDraftingId(s.id);
    try {
      const r = await draftFromSuggestion(s.id);
      if (r.recipe) onLoadDraft(r.recipe);
    } catch (e) { toast.error("No se pudo generar borrador: " + (e.message || e)); }
    finally { setDraftingId(null); }
  };

  const doDismiss = async (s) => {
    if (!window.confirm(`¿Descartar la sugerencia "${s.recipe_name}"?`)) return;
    setDismissingId(s.id);
    try {
      await dismissSuggestion(s.id);
      setSuggestions((xs) => xs.filter((x) => x.id !== s.id));
      toast.success("Descartada");
    } catch (e) { toast.error("No se pudo: " + (e.message || e)); }
    finally { setDismissingId(null); }
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <button data-testid="suggestions-back" onClick={onBack} className="btn-ghost">
          <ChevronLeft className="w-4 h-4" /> Volver
        </button>
        <button data-testid="suggestions-reload" onClick={load} className="text-xs italic text-amber-deep hover:underline">
          Recargar
        </button>
      </div>

      {loading && <p className="text-center italic text-cocoa-soft py-10">Cargando…</p>}
      {!loading && suggestions.length === 0 && (
        <div className="text-center py-16 bg-cream border border-line rounded-lg">
          <MessageSquare className="w-12 h-12 text-amber-deep mx-auto mb-4" />
          <p className="font-serif italic text-cocoa text-lg">Sin sugerencias por ahora.</p>
          <p className="font-serif italic text-cocoa-soft text-sm">Cuando la familia mande una, aparece acá.</p>
        </div>
      )}

      <ul data-testid="suggestions-list" className="space-y-3">
        {suggestions.map((s) => (
          <li key={s.id} data-testid={`suggestion-${s.id}`} className="bg-cream border border-line rounded-xl p-4">
            <div className="flex justify-between items-start gap-3 mb-2">
              <div className="flex-1 min-w-0">
                <p className="eyebrow text-[10px]">
                  De <b>{s.from_display || s.from_member}</b> · {new Date(s.created_at).toLocaleDateString("es-CL")}
                </p>
                <h3 className="font-display italic text-cocoa text-xl leading-tight">{s.recipe_name}</h3>
              </div>
            </div>
            <p className="font-serif text-cocoa text-sm mb-2 whitespace-pre-wrap">{s.reason}</p>
            {s.notes && (
              <div className="mt-2 p-3 bg-paper-warm rounded border border-line">
                <p className="text-xs italic text-cocoa-soft mb-1">Notas / link:</p>
                <p className="font-serif text-cocoa text-xs whitespace-pre-wrap break-words">{s.notes}</p>
              </div>
            )}
            <div className="flex gap-2 mt-4">
              <button
                data-testid={`suggestion-${s.id}-draft`}
                onClick={() => doDraft(s)}
                disabled={draftingId === s.id}
                className="btn-primary flex-1"
              >
                {draftingId === s.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
                {draftingId === s.id ? "Papi está armando…" : "Generar borrador con Papi"}
              </button>
              <button
                data-testid={`suggestion-${s.id}-dismiss`}
                onClick={() => doDismiss(s)}
                disabled={dismissingId === s.id}
                className="btn-ghost"
                title="Descartar"
              >
                {dismissingId === s.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}



// ==== Regenerar saludos de bienvenida (voz PVC) =====================
const WELCOME_MEMBERS = [
  { nick: "papa", label: "Papá (Pollo)" },
  { nick: "mama", label: "Mamá" },
  { nick: "tati", label: "Tati" },
  { nick: "lucho", label: "Lucho" },
  { nick: "tairuchi", label: "Tairuchi (Tair)" },
  { nick: "liuchi", label: "Liuchi" },
];

function WelcomeAudios({ onBack }) {
  const [busy, setBusy] = useState(null); // {nick, variant}
  const [lastResult, setLastResult] = useState(null);

  const doRegen = async (nick, variant) => {
    const label = variant === "short" ? "corto (vueltas siguientes)" : "largo (primera vez)";
    if (!window.confirm(`¿Regenerar el audio ${label} de ${nick}? Usa créditos de ElevenLabs.`)) return;
    setBusy({ nick, variant });
    setLastResult(null);
    try {
      const res = await regenerateWelcomeAudio(nick, variant);
      setLastResult({ nick, variant, ok: true, text: res.text_used, replaced: res.replaced });
      toast.success(`Audio ${variant} de ${nick} regenerado — Vercel redeploya en ~1 min`);
    } catch (e) {
      setLastResult({ nick, variant, ok: false, error: e.message || String(e) });
      toast.error("No se pudo: " + (e.message || e));
    } finally { setBusy(null); }
  };

  return (
    <div>
      <div className="mb-4">
        <button data-testid="welcome-back" onClick={onBack} className="btn-ghost">
          <ChevronLeft className="w-4 h-4" /> Volver
        </button>
      </div>

      <div className="bg-cream border border-line rounded-xl p-4 mb-6">
        <p className="font-serif italic text-cocoa text-sm mb-2">
          Cada miembro tiene dos saludos con tu voz clonada:
        </p>
        <ul className="text-xs italic text-cocoa-soft font-serif list-disc pl-5 space-y-1">
          <li><b>Largo</b>: suena la PRIMERA vez que ese miembro entra en un dispositivo.</li>
          <li><b>Corto</b>: suena en TODAS las vueltas siguientes ({'"qué bueno que volviste, disfruta"'}).</li>
        </ul>
        <p className="font-serif italic text-cocoa-soft text-xs mt-2">
          Los textos están en `worker.js` (`WELCOME_TEXTS` y `WELCOME_SHORT_TEXTS`). Cambia ahí + `wrangler deploy` antes de regenerar si quieres cambiar el guion.
        </p>
      </div>

      <ul data-testid="welcome-list" className="space-y-4">
        {WELCOME_MEMBERS.map((m) => (
          <li key={m.nick} data-testid={`welcome-${m.nick}`} className="bg-cream border border-line rounded-xl p-4">
            <h3 className="font-display italic text-cocoa text-xl mb-3">{m.label}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="p-3 rounded-lg bg-paper border border-line">
                <p className="eyebrow text-[10px] mb-1">Largo (primera vez)</p>
                <audio
                  data-testid={`welcome-${m.nick}-full-preview`}
                  controls preload="none"
                  src={`/audio/welcome/welcome_${m.nick}.mp3`}
                  className="w-full h-8 mb-2"
                />
                <button
                  data-testid={`welcome-${m.nick}-full-regen`}
                  onClick={() => doRegen(m.nick, "full")}
                  disabled={busy !== null}
                  className="btn-primary w-full text-xs"
                >
                  {busy?.nick === m.nick && busy?.variant === "full" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />}
                  {busy?.nick === m.nick && busy?.variant === "full" ? "Generando…" : "Regenerar largo"}
                </button>
              </div>
              <div className="p-3 rounded-lg bg-paper border border-line">
                <p className="eyebrow text-[10px] mb-1">Corto (vueltas siguientes)</p>
                <audio
                  data-testid={`welcome-${m.nick}-short-preview`}
                  controls preload="none"
                  src={`/audio/welcome/welcome_${m.nick}_short.mp3`}
                  className="w-full h-8 mb-2"
                />
                <button
                  data-testid={`welcome-${m.nick}-short-regen`}
                  onClick={() => doRegen(m.nick, "short")}
                  disabled={busy !== null}
                  className="btn-primary w-full text-xs"
                >
                  {busy?.nick === m.nick && busy?.variant === "short" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />}
                  {busy?.nick === m.nick && busy?.variant === "short" ? "Generando…" : "Regenerar corto"}
                </button>
              </div>
            </div>
          </li>
        ))}
      </ul>

      {lastResult && (
        <div className={`mt-6 p-4 rounded-xl border ${lastResult.ok ? "bg-cream border-amber-deep/40" : "bg-red-50 border-red-400"}`}>
          <h4 className="font-serif font-semibold text-cocoa mb-2">
            {lastResult.ok
              ? `✅ ${lastResult.nick} — ${lastResult.variant} (${lastResult.replaced ? "reemplazado" : "creado"})`
              : `❌ Error con ${lastResult.nick} (${lastResult.variant})`}
          </h4>
          {lastResult.ok
            ? <p className="text-xs italic text-cocoa-soft whitespace-pre-wrap">{lastResult.text}</p>
            : <p className="text-xs text-red-700">{lastResult.error}</p>}
        </div>
      )}
    </div>
  );
}

